import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from './base.service';
import { environment } from '../../environments/environment';
import { BaseQueryParams } from '../models/BaseQueryParams';
import { CompanyYear } from '../models/CompanyYear';
import { CompanyYearBusinessFeatureRange } from '../models/CompanyYearBusinessFeatureRange';

@Injectable({
  providedIn: 'root',
})
export class CompanyYearBusinessFeatureRangeService extends BaseService<CompanyYearBusinessFeatureRange> {
  private readonly localData: CompanyYearBusinessFeatureRange[];
  private localDataSubject: BehaviorSubject<CompanyYearBusinessFeatureRange[]>;

  constructor() {
    super(
      inject(HttpClient),
      `${environment.apiUrl}/company-year-business-feature-range/v1`,
      new BaseQueryParams('', 'year', 0, 100, false, true),
    );
    this.localData = [];
    this.localDataSubject = new BehaviorSubject<
      CompanyYearBusinessFeatureRange[]
    >([]);
    this.data$ = this.localDataSubject.asObservable();
  }

  /**
   * Overrides BaseService Create. This feature needed to update potentially multiple records
   * without affecting any other pending changes.  It will not refresh the data in between update/create calls.
   * @param {CompanyYearBusinessFeatureRange} range
   * @returns {Observable<HttpResponse<CompanyYearBusinessFeatureRange>>}
   */
  public override create(
    range: CompanyYearBusinessFeatureRange,
  ): Observable<HttpResponse<CompanyYearBusinessFeatureRange>> {
    return this.httpClient
      .post<CompanyYearBusinessFeatureRange>(this.baseApiUrl, range, {
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.ok) {
            this.localData.push(
              response.body as CompanyYearBusinessFeatureRange,
            );
          }
        }),
      );
  }

  declare public data$: Observable<CompanyYearBusinessFeatureRange[]>;

  /**
   * Overrides BaseService Update. This feature needed to update potentially multiple records
   * without affecting any other pending changes.  It will not refresh the data in between update/create calls.
   * @param {CompanyYearBusinessFeatureRange} range
   * @returns {Observable<HttpResponse<CompanyYearBusinessFeatureRange>>}
   */
  public override update(
    range: CompanyYearBusinessFeatureRange,
  ): Observable<HttpResponse<CompanyYearBusinessFeatureRange>> {
    return this.httpClient
      .put<CompanyYearBusinessFeatureRange>(this.baseApiUrl, range, {
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<CompanyYearBusinessFeatureRange>) => {
          if (response.ok) {
            const idx = this.localData.indexOf(
              this.localData.find((x) => x) as CompanyYearBusinessFeatureRange,
            );
            this.localData[idx] =
              response.body as CompanyYearBusinessFeatureRange;
          }
        }),
      );
  }

  /**
   * Get Business feature ranges for a given company year.  Include back years back, and forward years forward.
   * @param {CompanyYear} companyYear - the current company year that is being targeted.
   * @param {number} back (optional) - number of years back to also include.
   * @param {number} forward (optional) - number of years forward to also include.
   */
  public getByCompanyYear(
    companyYear: CompanyYear,
    back?: number,
    forward?: number,
    businessFeatureId?: number,
  ): void {
    const params = new HttpParams()
      .set('back', back ?? 1)
      .set('forward', forward ?? 1)
      .set('businessFeatureId', businessFeatureId ?? '');
    const apiUrl = `${this.baseApiUrl}/ranges/${companyYear.id}`;
    this.httpClient
      .get<
        CompanyYearBusinessFeatureRange[]
      >(apiUrl, { params, observe: 'response' })
      .subscribe((response) => {
        this.localDataSubject.next(response?.body || []);
      });
  }

  public getCurrentByBusinessFeatureName(
    businessFeatureName: string,
  ): Observable<CompanyYearBusinessFeatureRange> {
    const url = `${this.baseApiUrl}/getCurrentCompanyYear`;
    const params = new HttpParams().set(
      'businessFeatureName',
      businessFeatureName,
    );
    return this.httpClient.get<CompanyYearBusinessFeatureRange>(url, {
      params,
    });
  }

  public getByBusinessFeatureNameCompanyYearId(
    businessFeatureName: string,
    companyYearId: number,
  ): Observable<CompanyYearBusinessFeatureRange> {
    const url = `${this.baseApiUrl}/byBusinessFeatureNameCompanyYearId`;
    const params = new HttpParams()
      .set('businessFeatureName', businessFeatureName)
      .set('companyYearId', companyYearId);
    return this.httpClient
      .get<CompanyYearBusinessFeatureRange>(url, { params })
      .pipe();
  }

  public getByBusinessFeatureNameCompanyYear(
    businessFeatureName: string,
    companyYear: number,
  ): Observable<CompanyYearBusinessFeatureRange> {
    const url = `${this.baseApiUrl}/byBusinessFeatureNameCompanyYear`;
    const params = new HttpParams()
      .set('businessFeatureName', businessFeatureName)
      .set('companyYear', companyYear);
    return this.httpClient
      .get<CompanyYearBusinessFeatureRange>(url, { params })
      .pipe();
  }

  public allowEditCompanyYearId(
    companyYearId: number,
    businessFeatureName: string,
  ): Observable<boolean> {
    this.loadingSubject.next(true);
    return forkJoin([
      this.getCurrentByBusinessFeatureName(businessFeatureName),
      this.getByBusinessFeatureNameCompanyYearId(
        businessFeatureName,
        companyYearId,
      ),
    ]).pipe(
      map(([current, selected]) => {
        this.loadingSubject.next(false);
        return selected?.startDate >= current?.startDate;
      }),
    );
  }

  public allowEditCompanyYear(
    companyYear: number,
    businessFeatureName: string,
  ): Observable<boolean> {
    this.loadingSubject.next(true);
    return forkJoin([
      this.getCurrentByBusinessFeatureName(businessFeatureName),
      this.getByBusinessFeatureNameCompanyYear(
        businessFeatureName,
        companyYear,
      ),
    ]).pipe(
      map(([current, selected]) => {
        this.loadingSubject.next(false);
        return selected?.startDate >= current?.startDate;
      }),
    );
  }
}
